"use client";
import { useRef, ChangeEvent, DragEvent, useEffect } from "react";

export type FileWithPreview = {
  file: File | null;   // null kalau file lama
  preview: string;     // URL atau objectURL
  isNew?: boolean;     // true kalau file baru
};

type Props = {
  files: FileWithPreview[];
  onChange: (files: FileWithPreview[]) => void;
  maxFiles?: number;
  profileImages?: string[]; // foto lama dari profile
  modalOpen?: boolean;      // supaya sync hanya saat modal dibuka
};

export default function FilePondUploader({
  files,
  onChange,
  maxFiles = 5,
  profileImages = [],
  modalOpen = false,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  // ⚡ Sync foto lama dari profile saat modal dibuka
  useEffect(() => {
    if (modalOpen) {
      const oldFiles: FileWithPreview[] = profileImages.map(url => ({
        file: null,
        preview: url,
        isNew: false
      }));

      // merge foto lama + yang sudah ada di state (foto baru)
      const newState = [...oldFiles, ...files.filter(f => f.isNew)];
      onChange(maxFiles ? newState.slice(0, maxFiles) : newState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  const addFiles = (newFiles: FileList) => {
    const arr: FileWithPreview[] = Array.from(newFiles).map(f => ({
      file: f,
      preview: URL.createObjectURL(f),
      isNew: true
    }));

    // merge file lama + file baru
    const updated = [...files, ...arr];

    // slice hanya jika melewati maxFiles
    onChange(maxFiles ? updated.slice(0, maxFiles) : updated);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) addFiles(e.target.files);
  };

  const removeFile = (index: number) => {
    const updated = files.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <div>
      <div
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary"
        onClick={() => inputRef.current?.click()}
      >
        Drag & Drop or Click to Browse
      </div>

      <input
        type="file"
        multiple
        accept="image/*"
        ref={inputRef}
        className="hidden"
        onChange={handleInputChange}
      />

      <div className="mt-4 grid grid-cols-3 gap-4 max-h-80 overflow-y-auto">
        {files.map((f, idx) => (
          <div key={idx} className="relative group">
            <img
              src={f.preview}
              alt={`preview-${idx}`}
              className="w-full h-24 object-cover rounded-lg"
            />
            <button
              onClick={() => removeFile(idx)}
              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
            >
              &times;
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
